import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  FormHelperText,
  Divider,
} from "@mui/material";
import React, { Fragment } from "react";
import { Info, Upload, XCircle } from "react-feather";
import { Controller as Fc } from "react-hook-form";
import { useTranslation } from "react-i18next";
import StepperGuide from "src/components/StepperGuide";
import TreffasTextField from "src/components/TreffasTextField";
import TreffasStyle from "src/utils/TreffasStyle";
import PackageAttachments from "./PackageAttachments";
import Agreement from "../individual-booking/schedule/agreement";
import VisuallyHiddenInput from "src/components/VisuallyHiddenInput";
import Helper from "src/utils/Helper";
import MobileCodes from "src/components/MobileCodes";
import TreffasTooltip from "src/components/TreffasTooltip";
import { useFacebookPixelPublic } from "src/views/hooks/useFacebookPixelPublic";
import ChooseVideoLink from "src/views/general/booking-components/ChooseVideoLink";
import { useGoogleAnalyticsPublic } from "src/views/hooks/useGoogleAnalyticsPublic";
import AddGuest from "./AddGuest";

const BookingBasicInformationForm = ({
  errors,
  control,
  selectedOffer,
  clearErrors,
  hideThisSurvey,
  nextButtonRef,
  stepperGuideLength,
  isProcess,
  submitButtonRef,
  setValue,
  handleBack,
  _display,
  extraFieldSelectedFiles,
  setExtraFieldSelectedFiles,
  selectedDialCode,
  setSelectedDialCode,
  coachDetails,
  conferenceType,
  setConferenceType,
  customStyle,
  showReason = false, // use for reschedule booking
  guests,
  setGuests,
}) => {
  const { t } = useTranslation();

  const handleSelectedFiles = (e, field_name) => {
    let selectedFiles = e.target.files;

    if (selectedFiles.length === 0) return;

    delete errors[field_name];

    setExtraFieldSelectedFiles((prev) => {
      let olddata = prev[field_name] || [];

      return {
        ...prev,
        [field_name]: [...olddata, ...selectedFiles],
      };
    });
  };

  let user_settings = null;
  if (coachDetails?.settings) {
    if (typeof coachDetails.settings === "string") {
      try {
        user_settings = JSON.parse(coachDetails.settings);
      } catch (e) {
        user_settings = null;
      }
    } else {
      user_settings = coachDetails.settings;
    }
  }

  const __analytics = user_settings?.analytics ?? null;
  let enable = __analytics?.has_meta_pixel ?? false;
  let pixelId = __analytics?.meta_pixel ?? null;

  let googleAnalyticsEnable = __analytics?.has_google_analytics ?? false;
  let gaId = __analytics?.google_analytics ?? null;

  const trackEventInFacebook = useFacebookPixelPublic({
    track: "ViewContent",
    content_name: `${selectedOffer?.name ?? ""} Create booking Form`,
    content_description: `${selectedOffer?.description ?? "Package create booking form."
      }`,
    enable: enable,
    pixelId: pixelId,
  });

  const trackEventInGoogleAnalytics = useGoogleAnalyticsPublic({
    enable: googleAnalyticsEnable,
    gaId: gaId,
  });

  React.useEffect(() => {
    trackEventInFacebook();

    trackEventInGoogleAnalytics("view_content", {
      content_name: `${selectedOffer?.name ?? ""} Create booking Form`,
      content_description: `${selectedOffer?.description ?? "Package create booking form."
        }`,
    });
  }, []);

  const onhandleEvent = () => {
    submitButtonRef.current.click();
  };

  return (
    <Box
      sx={{
        pt: "58px",
      }}
    >
      <Box>
        <Grid container spacing={"20px"}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                mb: "5px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: "20px",
                }}
              >
                {/* {(_display === "embeded" || isIframe) && (
                  <Box>
                    <IconButton onClick={() => handleBack()}>
                      <ArrowLeft />
                    </IconButton>
                  </Box>
                )} */}

                <Box>
                  <Typography
                    sx={{
                      fontFamily: `var(--font-${customStyle?.headingFont})`,
                      fontSize: "18px",
                      lineHeight: "23px",
                      alignItems: "center",
                      color: customStyle?.textColor ?? "var(--color-black)",
                    }}
                  >
                    {t(
                      "individual.booking.schedule.choose_schedule.client_details"
                    )}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: customStyle?.secondaryTextColor ?? "#878787",
                      fontFamily: `var(--font-${customStyle?.bodyFont})`,
                    }}
                  >
                    {t(
                      "individual.booking.schedule.choose_schedule.final_step"
                    )}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <StepperGuide
                  length={stepperGuideLength}
                  activeLength={[1, 2]}
                  customStyle={customStyle}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                  color: customStyle?.secondaryTextColor ?? "#878787",
                  fontFamily: `var(--font-${customStyle?.bodyFont})`,
                }}
              >
                {" "}
                {t("booking.label.client_information.firstname")}{" "}
              </Typography>

              <Fc
                name="firstname"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TreffasTextField
                    {...field}
                    fullWidth
                    placeholder="Alex"
                    type="text"
                    inputProps={{ "data-testid": "public-booking-firstname" }}
                    sx={TreffasStyle.formInputLarge("input")}
                    error={Boolean(errors.firstname)}
                    helperText={errors.firstname?.message}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                  color: customStyle?.secondaryTextColor ?? "#878787",
                  fontFamily: `var(--font-${customStyle?.bodyFont})`,
                }}
              >
                {" "}
                {t("booking.label.client_information.lastname")}{" "}
              </Typography>

              <Fc
                name="lastname"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TreffasTextField
                    {...field}
                    fullWidth
                    placeholder="Smith"
                    inputProps={{ "data-testid": "public-booking-lastname" }}
                    type="text"
                    sx={TreffasStyle.formInputLarge("input")}
                    error={Boolean(errors.lastname)}
                    helperText={errors.lastname?.message}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box>
                {" "}
                <Typography
                  sx={{
                    ...TreffasStyle.formLabel,
                    color: customStyle?.secondaryTextColor ?? "#878787",
                    fontFamily: `var(--font-${customStyle?.bodyFont})`,
                  }}
                >
                  {t("booking.label.client_information.phonenumber")}
                </Typography>{" "}
              </Box>

              <Box>
                {" "}
                <TreffasTooltip
                  title={`Enter your mobile number to receive timely reminders and notifications.`}
                  customStyle={customStyle}
                />{" "}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #DFE2E6",
                borderRadius: "8px",
                backgroundColor: "#FFF",
              }}
            >
              <Box sx={{ marginLeft: "10px", minWidth: "150px" }}>
                <MobileCodes
                  value={selectedDialCode}
                  setValue={setSelectedDialCode}
                />
              </Box>

              <Box
                sx={{
                  ml: "10px",
                }}
              >
                <Divider orientation="vertical" sx={{ height: "30px" }} />
              </Box>

              <Box width={"100%"}>
                <Fc
                  name="mobile"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <TreffasTextField
                      variant="standard"
                      {...field}
                      fullWidth
                      placeholder={"Enter mobile"}
                      sx={TreffasStyle.formInputLarge("input")}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                sx={{
                  ...TreffasStyle.formLabel,
                  color: customStyle?.secondaryTextColor ?? "#878787",
                  mb: "1px",
                  fontFamily: `var(--font-${customStyle?.bodyFont})`,
                }}
              >
                {" "}
                {t("booking.label.client_information.email")}{" "}
              </Typography>

              <Fc
                name="email"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TreffasTextField
                    {...field}
                    fullWidth
                    placeholder="alexsmith@gmail.com"
                    type="text"
                    inputProps={{ "data-testid": "public-booking-email" }}
                    sx={TreffasStyle.formInputLarge("input")}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <AddGuest guests={guests} setGuests={setGuests} />
          </Grid>

          {/* additional fields */}
          {(selectedOffer?.fields?.length > 0 ||
            selectedOffer?.custom_options?.package_files?.length > 0) && (
              <Grid item xs={12}>
                <Box mt={"12px"}>
                  <Typography
                    sx={{
                      fontFamily: `var(--font-${customStyle?.headingFont})`,
                      fontSize: "18px",
                      lineHeight: "31px",
                      color: customStyle?.textColor ?? "var(--color-black)",
                    }}
                  >
                    {t("booking.label.additional_information")}
                  </Typography>
                </Box>
              </Grid>
            )}

          {selectedOffer?.fields?.map((extra, key) => {
            let options = extra?.options ? JSON.parse(extra.options) : null;
            let uploadedFile =
              extraFieldSelectedFiles[Helper.createSlug(extra.name)] ?? [];
            const isUpload = extra.type === "upload" ? true : false;

            return (
              <Fragment key={key}>
                <Grid item xs={12}>
                  <Box>
                    <Typography
                      sx={{
                        ...TreffasStyle.formLabel,
                        textTransform: "capitalize",
                        fontFamily: `var(--font-${customStyle?.bodyFont})`,
                      }}
                    >
                      {extra.name?.toLowerCase()}

                      {options?.has_description && (
                        <Typography
                          variant={"caption"}
                          sx={{ cursor: "help", ml: "10px" }}
                        >
                          <Tooltip arrow title={options?.description}>
                            <Info size={15} color={"#6E757C"} />
                          </Tooltip>
                        </Typography>
                      )}
                    </Typography>

                    {isUpload &&
                      Boolean(errors[Helper.createSlug(extra.name)]) && (
                        <FormHelperText error>
                          {errors[Helper.createSlug(extra.name)]?.message}{" "}
                        </FormHelperText>
                      )}

                    {extra.type === "option" ? (
                      <Fc
                        name={Helper.createSlug(extra.name)}
                        control={control}
                        rules={{
                          required: Boolean(parseInt(extra.required)),
                        }}
                        render={({ field }) => {
                          return (
                            <TreffasTextField
                              {...field}
                              placeholder={`Please select your answer...`}
                              fullWidth
                              type={"text"}
                              variant="outlined"
                              sx={{
                                ...TreffasStyle.formInputLarge("input"),
                                "& .MuiNativeSelect-select": {
                                  textTransform: "capitalize",
                                },
                              }}
                              select
                              SelectProps={{
                                native: true,
                              }}
                              error={Boolean(
                                errors[Helper.createSlug(extra.name)]
                              )}
                              helperText={
                                errors[Helper.createSlug(extra.name)]?.message
                              }
                              value={field.value ?? ""}
                            >
                              <option value={""}> Select </option>
                              {JSON.parse(extra.values).map((option, key) => (
                                <option
                                  key={key}
                                  values={option?.value ?? option}
                                  style={{
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {option?.value ?? option}
                                </option>
                              ))}
                            </TreffasTextField>
                          );
                        }}
                      />
                    ) : isUpload ? (
                      <Box>
                        <Box>
                          <Button
                            component="label"
                            variant="contained"
                            startIcon={<Upload size={18} />}
                            fullWidth
                          >
                            Upload file
                            <VisuallyHiddenInput
                              type="file"
                              multiple
                              onChange={(e) =>
                                handleSelectedFiles(
                                  e,
                                  Helper.createSlug(extra.name)
                                )
                              }
                            />
                          </Button>
                        </Box>

                        <Box>
                          {Array.from(uploadedFile).map((file, xfile) => {
                            return (
                              <Box
                                key={xfile}
                                sx={{
                                  m: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box>
                                  &bull;{" "}
                                  <Typography
                                    variant={"caption"}
                                    sx={{
                                      fontFamily: `var(--font-${customStyle?.bodyFont})`,
                                    }}
                                  >
                                    {file.name}
                                  </Typography>
                                </Box>

                                <Box sx={{ maxWidth: "20px" }}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      let newFile = Array.from(
                                        uploadedFile
                                      )?.filter((x, key) => key !== xfile);

                                      setExtraFieldSelectedFiles((prev) => {
                                        return {
                                          ...prev,
                                          [Helper.createSlug(extra.name)]:
                                            newFile,
                                        };
                                      });
                                    }}
                                  >
                                    <XCircle size={18} />
                                  </IconButton>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ) : (
                      <Fc
                        name={Helper.createSlug(extra.name)}
                        control={control}
                        rules={{
                          required: Boolean(parseInt(extra.required)),
                        }}
                        render={({ field }) => {
                          return (
                            <TreffasTextField
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                field.onChange(e);
                                clearErrors(`${Helper.createSlug(extra.name)}`);
                              }}
                              placeholder={`Please write your answer...`}
                              fullWidth
                              type={extra.type === "email" ? "email" : "text"}
                              variant="outlined"
                              sx={TreffasStyle.formInputLarge(
                                extra.type === "paragraph"
                                  ? "textarea"
                                  : "input"
                              )}
                              error={Boolean(
                                errors[Helper.createSlug(extra.name)]
                              )}
                              helperText={
                                errors[Helper.createSlug(extra.name)]?.message
                              }
                              multiline={
                                extra.type === "paragraph" ? true : false
                              }
                              minRows={extra.type === "paragraph" ? 3 : 0}
                            />
                          );
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              </Fragment>
            );
          })}
          {/* additional attachments */}
          {selectedOffer?.custom_options?.package_files && (
            <PackageAttachments
              package_files={
                JSON.parse(selectedOffer?.custom_options?.package_files) ?? []
              }
            />
          )}
        </Grid>

        {showReason && (
          <Grid item xs={12} md={12}>
            <Box sx={{ mt: "12px" }}>
              <Typography sx={TreffasStyle.formLabel}>
                {t("individual.booking.schedule.reschedule.reason")}
              </Typography>

              <Fc
                name="reason"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <TreffasTextField
                    {...field}
                    fullWidth
                    placeholder={t(
                      "individual.booking.schedule.placeholder.reason_for_change"
                    )}
                    type="text"
                    sx={(themes) => ({
                      ...TreffasStyle.formInput(themes),
                      ...TreffasStyle.formInputLarge("textarea"),
                    })}
                    error={Boolean(errors.reason)}
                    helperText={errors.reason?.message}
                    multiline
                    minRows={3}
                  />
                )}
              />
            </Box>
          </Grid>
        )}
      </Box>

      <Box>
        <ChooseVideoLink
          conferenceType={conferenceType}
          setConferenceType={setConferenceType}
          selectedOffer={selectedOffer}
          customStyle={customStyle}
        />
      </Box>

      <Box sx={{ mt: "32px" }}>
        <Box>
          <Agreement
            errors={errors}
            setValue={setValue}
            coachDetails={coachDetails}
            customStyle={customStyle}
          />
        </Box>

        <Box
          sx={{
            mt: "30px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
            width: "100%",
          }}
        >
          {!hideThisSurvey && selectedOffer?.survey?.length > 0 && (
            <Button
              variant={"contained"}
              sx={{
                height: "48px",
                borderRadius: "12px",
                fontSize: "14px",
                backgroundColor:
                  customStyle?.accentColor ?? "var(--color-black)",
                color: customStyle?.buttonTextColor ?? "#fff",

                "&:hover": {
                  backgroundColor: `${customStyle?.accentColor} !important`,
                  color: customStyle?.buttonTextColor ?? "#fff",
                },
                borderColor: "var(--color-black)",
              }}
              onClick={() => nextButtonRef.current.click()}
            >
              {t(
                "individual.booking.schedule.choose_schedule.button.continue_survey"
              )}
            </Button>
          )}

          <Button
            variant={"contained"}
            data-testid="public-booking-schedule-event-btn"
            sx={{
              height: "48px",
              borderRadius: "12px",
              fontSize: "14px",
              display:
                hideThisSurvey && selectedOffer?.survey?.length > 0
                  ? "inline-flex"
                  : selectedOffer?.survey?.length === 0
                    ? "inline-flex"
                    : "none",
              backgroundColor: customStyle?.accentColor ?? "var(--color-black)",
              color: customStyle?.buttonTextColor ?? "#fff",
              border: "1px solid",
              borderColor: `${customStyle?.accentColor} !important`,

              "&:hover": {
                backgroundColor: `${customStyle?.accentColor} !important`,
                color: customStyle?.buttonTextColor ?? "#fff",
                border: "1px solid",
                borderColor: `${customStyle?.accentColor} !important`,
              },
            }}
            onClick={() => onhandleEvent()}
            disabled={isProcess}
            startIcon={
              isProcess && <CircularProgress size={15} color="inherit" />
            }
          >
            {t(
              "individual.booking.schedule.choose_schedule.button.schedule_event"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BookingBasicInformationForm;
